import { toGoLangInvoke } from "../common/windowFunctions";
import { logStatus } from "../utils";
export class BloombergBridgeClientImpl {
    constructor(gluePromise, asyncConstructors, loggerClient) {
        this.gluePromise = gluePromise;
        this.loggerClient = loggerClient;
        this.groupEventListener = null;
        const pw = asyncConstructors.addConstructor();
        this.gluePromise.then((g) => {
            this.glue = g;
            this.setupForeverListeners();
            asyncConstructors.finishConstructor(pw);
        });
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "AuthenticationClient",
            method, type, data
        });
    }
    async checkIfBridgeIsRunning() {
        const methods = this.glue.interop.methods();
        return methods.some(({ name }) => name === "T42.BBG.IsLoggedIn");
    }
    async setupForeverListeners() {
        try {
            const subscription = await this.glue.interop.subscribe("T42.BBG.GroupsChanged");
            subscription.onData((streamData) => {
                if (!this.groupEventListener) {
                    return;
                }
                this.groupEventListener(null, {
                    data: {
                        group: streamData.data,
                    },
                });
            });
        }
        catch (error) {
        }
    }
    setEnabled(enabled, cb) {
        console.info("method 'setEnabled' has not been implemented.");
    }
    async checkConnection(cb) {
        this.logStatus("checkConnection", "start");
        if (await this.checkIfBridgeIsRunning()) {
            const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.BBG.IsLoggedIn"));
            const isConnected = data?.Result;
            this.logStatus("checkConnection", "return", { isConnected });
            cb(err, isConnected);
        }
        else {
            cb("Unable to connect", false);
            this.logStatus("checkConnection", "return", { isConnected: false });
        }
    }
    setConnectionEventListener(cb) {
        this.logStatus("setConnectionEventListener", "deprecated", "Not implemented");
        console.info("method 'setConnectionEventListener' has not been implemented.");
    }
    removeConnectionEventListener() {
        this.logStatus("removeConnectionEventListener", "deprecated", "Not implemented");
        console.info("method 'removeConnectionEventListener' has not been implemented.");
    }
    async runGetAllGroups(cb) {
        this.logStatus("runGetAllGroups", "start");
        if (await this.checkIfBridgeIsRunning()) {
            const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.BBG.GetGroupsInfo"));
            this.logStatus("runGetAllGroups", "return", data?.groups);
            cb(err, {
                status: true,
                groups: data?.groups,
            });
        }
        else {
            cb("Unable to connect", null);
        }
    }
    async runGetGroupContext(groupName, cb) {
        this.logStatus("runGetGroupContext", "start");
        if (await this.checkIfBridgeIsRunning()) {
            const invocationOptions = { group: groupName };
            const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.BBG.GetGroupContext", invocationOptions));
            this.logStatus("runGetGroupContext", "return", data?.result);
            cb(err, { status: true, group: data?.result });
        }
        else {
            cb("Unable to connect", null);
        }
    }
    async runSetGroupContext(groupName, value, cookie, cb) {
        this.logStatus("runSetGroupContext", "start", { groupName, value, cookie });
        if (await this.checkIfBridgeIsRunning()) {
            const { err } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.BBG.SetGroupContext", {
                group: groupName,
                context: value,
                cookie,
            }));
            this.logStatus("runSetGroupContext", "return", { status: true });
            cb(err, { status: true });
        }
        else {
            cb("Unable to connect", null);
        }
    }
    async setGroupEventListener(cb) {
        this.logStatus("setGroupEventListener", "start");
        this.groupEventListener = cb;
        this.logStatus("setGroupEventListener", "return");
    }
    removeGroupEventListener() {
        this.logStatus("removeGroupEventListener", "start");
        this.groupEventListener = null;
        this.logStatus("removeGroupEventListener", "return");
    }
    async runGetAllWorksheets(cb) {
        this.logStatus("runGetAllWorksheets", "start");
        if (await this.checkIfBridgeIsRunning()) {
            const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.BBG.GetWorksheets"));
            const worksheets = data?.worksheets;
            this.logStatus("runGetAllWorksheets", "return", worksheets);
            cb(err, {
                status: true,
                worksheets,
            });
        }
        else {
            cb("Unable to connect", null);
        }
    }
    async runGetWorksheet(worksheetId, cb) {
        this.logStatus("runGetWorksheet", "start");
        if (await this.checkIfBridgeIsRunning()) {
            const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.BBG.GetWorksheetSecurities", { worksheetId }));
            this.logStatus("runGetWorksheet", "return", data);
            cb(err, {
                status: true,
                worksheet: data,
            });
        }
        else {
            cb("Unable to connect", null);
        }
    }
    async runCreateWorksheet(worksheetName, securities, cb) {
        this.logStatus("runCreateWorksheet", "start", { worksheetName, securities });
        if (await this.checkIfBridgeIsRunning()) {
            const invocationOptions = {
                name: worksheetName,
                securities,
            };
            const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.BBG.CreateWorksheet", invocationOptions));
            const worksheet = data?.worksheet;
            this.logStatus("runCreateWorksheet", "return", worksheet);
            cb(err, {
                status: true,
                worksheet,
            });
        }
        else {
            cb("Unable to connect", null);
        }
    }
    async runReplaceWorksheet(worksheetId, securities, cb) {
        this.logStatus("runReplaceWorksheet", "start", { worksheetId, securities });
        if (await this.checkIfBridgeIsRunning()) {
            const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.BBG.SetWorksheetSecurities", { securities, worksheetId }));
            this.logStatus("runReplaceWorksheet", "return", data?.worksheet);
            cb(err, {
                status: true,
                worksheet: data?.worksheet,
            });
        }
        else {
            cb("Unable to connect", null);
        }
    }
    async runBBGCommand(mnemonic, securities, panel, tails, cb) {
        this.logStatus("runBBGCommand", "start", { mnemonic, securities, panel, tails });
        if (await this.checkIfBridgeIsRunning()) {
            const { err } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.BBG.RunFunction", {
                mnemonic,
                panel,
                securities,
                tails,
            }));
            this.logStatus("runBBGCommand", "return");
            cb(err, { status: true });
        }
        else {
            cb("Unable to connect", null);
        }
    }
    async runSecurityLookup(security, cb) {
        this.logStatus("runSecurityLookup", "deprecated", "Not Implemented");
        if (await this.checkIfBridgeIsRunning()) {
            console.info("method 'runSecurityLookup' has not been implemented.");
        }
        else {
            cb("Unable to connect", null);
        }
    }
}
